'use client';

import { useState, useEffect, useRef, useMemo } from 'react';
import { ArrowLeft, Check } from 'lucide-react';
import { SelfieCapture } from './SelfieCapture';
import { PhotoResultsGrid } from './PhotoResultsGrid';
import { PhotoConsentGate } from './PhotoConsentGate';
import { PhotoEventHero } from './PhotoEventHero';
import { FacialRecognitionNotice } from './FacialRecognitionNotice';
import type { PhotoConsentInput } from '@/lib/race-photos/consent';
import { PhotoPurchaseForm } from './PhotoPurchaseModal';
import { formatPriceCents } from '@/lib/utils/currency';
import { useIsMobile } from '@/hooks/use-mobile';
import { photoRequestHeaders } from '@/lib/race-photos/client-id';
import { fetchThroughBusy } from '@/lib/race-photos/busy-retry';
import { mergePickedPhotos } from '@/lib/race-photos/browse-merge';
import { splitSearchByOwned, alreadyOwnedNote } from '@/lib/race-photos/owned-photos';
import { joinDownloadTokens } from '@/lib/race-photos/download-tokens';
import { EmailTypoHint } from '@/components/ui/email-typo-hint';
import { HubCodeInput, HubSkeleton, hubButtonStyle } from '@/components/hub/hub-ui';
import {
  PAGE_COLUMN,
  StepError,
  StepHeading,
  StepSpinner,
  StepText,
  stepInputStyle,
  stepLinkStyle,
  stepPrimaryStyle,
  TileTick,
  viewerActionStyle,
  viewerControlStyle,
  viewerSurfaceStyle,
  type PhotoVariant,
} from './photo-flow-ui';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

type Step = 'selfie' | 'searching' | 'results' | 'payment' | 'free_email' | 'no_results' | 'verify_email' | 'verify_code';

interface ThemeColors {
  primary: string;
  secondary: string;
  text: string;
  textMuted: string;
  buttonText?: string;
}

interface RacePhotoFinderProps {
  albumId: string;
  albumToken: string;
  /**
   * Where this finder's endpoints live. An album link passes
   * `/api/race-photos/<albumId>`; the event's master link passes
   * `/api/race-photos/event/<token>`. Defaults to the album form, so the album
   * page is unchanged.
   */
  apiBase?: string;
  /**
   * Master-link scope has no per-album shortcuts yet: finding your photos by
   * the email you registered with, and redeeming a pre-purchase, are both
   * album-scoped features. Hidden rather than offered and then failing.
   */
  supportsEmailLookup?: boolean;
  eventName: string;
  participantPrice: number;
  participantPriceCurrency: string;
  coverImageUrl?: string;
  /** optional phone crop; falls back to coverImageUrl */
  mobileCoverImageUrl?: string;
  /** Where the banner is anchored at each breakpoint (object-position). */
  bannerPosition?: { desktop: string; mobile: string };
  /** drives the removal route offered in the facial-recognition notice */
  selfRedactionEnabled?: boolean;
  /** where the notice sends someone who wants to be removed */
  removalHref?: string;
  themeColors: ThemeColors;
  initialDownloadToken?: string;
  initialEmail?: string;
  /** 'hub' draws the flow in the athlete hub's v8 look (photo-flow-ui.tsx). */
  variant?: PhotoVariant;
  /**
   * Whether `${apiBase}/browse` exists. Only the event's master link has it;
   * the album page asked for it on every visit and logged a 404 each time.
   * Defaults to true when an apiBase is given (the master link page).
   */
  supportsBrowse?: boolean;
  /**
   * The face templates have been deleted (12 months after the event, or the
   * organiser took the photos down). No consent, no camera: the page says so
   * and offers browsing, which still works.
   */
  faceSearchClosed?: boolean;
  /**
   * The signed-in athlete's email. The free download and the purchase use it
   * instead of asking for an address the hub already has.
   */
  knownEmail?: string;
  /**
   * Redeem a pre-purchase on the athlete's session (the hub), in place of the
   * emailed code. Given matched photo ids and the selfie key, resolves to the
   * download token or throws with a message to show.
   */
  redeemPrePurchase?: (matchedPhotoIds: string[], selfieS3Key: string) => Promise<string>;
  /** Called once photos are unlocked (bought, free or redeemed). */
  onPhotosUnlocked?: () => void;
}

function formatPrice(cents: number, currency: string) {
  return formatPriceCents(cents, currency);
}

export function RacePhotoFinder({
  albumId,
  albumToken,
  apiBase,
  supportsEmailLookup = true,
  eventName,
  participantPrice,
  participantPriceCurrency,
  coverImageUrl,
  mobileCoverImageUrl,
  bannerPosition,
  selfRedactionEnabled = false,
  removalHref,
  themeColors,
  initialDownloadToken,
  initialEmail,
  variant = 'page',
  supportsBrowse,
  faceSearchClosed: faceSearchClosedAtLoad = false,
  knownEmail,
  redeemPrePurchase,
  onPhotosUnlocked,
}: RacePhotoFinderProps) {
  const hub = variant === 'hub';
  const browseSupported = supportsBrowse ?? !!apiBase;
  // One base for every call, so this component works for an album link and the
  // event's master link without knowing which it is inside.
  const base = apiBase ?? `/api/race-photos/${albumId}`;
  const isMobile = useIsMobile();
  // Recorded consent for facial-recognition search. Null until the gate is
  // completed: the camera never opens and no search fires without it.
  const [consent, setConsent] = useState<PhotoConsentInput | null>(null);
  const [step, setStep] = useState<Step>(
    initialDownloadToken ? 'results' : (initialEmail && supportsEmailLookup) ? 'verify_email' : 'selfie'
  );
  const [matchedPhotoIds, setMatchedPhotoIds] = useState<string[]>([]);
  const [previews, setPreviews] = useState<{ id: string; previewUrl: string | null; filename: string }[]>([]);
  /**
   * Photos that matched, but not confidently enough for us to hand over on our
   * own judgement. Raising the confident threshold stopped strangers appearing
   * in downloads, and necessarily costs some genuine matches too; these are
   * that band. The athlete decides, because they know what they look like.
   */
  const [possibleMatches, setPossibleMatches] = useState<{ id: string; previewUrl: string | null; filename: string }[]>([]);
  /**
   * Browsing every published photo, when the organiser has allowed it. The
   * fallback for the athlete face matching cannot help: photos with no
   * detectable face, and genuine matches a safe threshold has to miss.
   */
  const [browseEnabled, setBrowseEnabled] = useState(false);
  const [faceSearchClosed, setFaceSearchClosed] = useState(faceSearchClosedAtLoad);
  const [browsePhotos, setBrowsePhotos] = useState<{ id: string; previewUrl: string | null; fullPreviewUrl?: string | null; filename: string }[]>([]);
  const [browseCursor, setBrowseCursor] = useState<string | null>(null);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [pickedBrowse, setPickedBrowse] = useState<Set<string>>(new Set());
  /** Open in place under the results, rather than navigating away from them. */
  const [browseOpen, setBrowseOpen] = useState(false);
  /** Index into browsePhotos for the lightbox, or null when it is closed. */
  const [browseLightbox, setBrowseLightbox] = useState<number | null>(null);

  const loadBrowse = async (cursor?: string | null): Promise<number> => {
    setBrowseLoading(true);
    try {
      const url = new URL(`${base}/browse`, window.location.origin);
      if (cursor) url.searchParams.set('cursor', cursor);
      const res = await fetch(url.toString(), { headers: photoRequestHeaders(false) });
      if (!res.ok) return 0;
      const data = await res.json();
      setBrowseEnabled(!!data.enabled);
      const page = ((data.photos || []) as { id: string; previewUrl: string | null; fullPreviewUrl?: string | null; filename: string | null }[])
        .map((x) => ({ ...x, filename: x.filename || 'Race photo' }));
      setBrowsePhotos((prev) => (cursor ? [...prev, ...page] : page));
      setBrowseCursor(data.nextCursor || null);
      return page.length;
    } finally {
      setBrowseLoading(false);
    }
  };

  const toggleBrowsePick = (id: string) => {
    setPickedBrowse((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  /**
   * Step through the lightbox, pulling the next page in at the end rather than
   * stopping dead. Browsing is a hunt, and the hunt should not end at photo 60.
   */
  const moveLightbox = async (delta: number) => {
    if (browseLightbox === null) return;
    const next = browseLightbox + delta;
    if (next < 0) return;
    if (next >= browsePhotos.length) {
      if (!browseCursor || browseLoading) return;
      const added = await loadBrowse(browseCursor);
      if (added > 0) setBrowseLightbox(next);
      return;
    }
    setBrowseLightbox(next);
  };

  /** Where a swipe began, so a drag across the photo can page it on a phone. */
  const browseTouchRef = useRef<{ x: number; y: number } | null>(null);
  /**
   * Zoomed into the photo currently open. A race photo shown to fit a 390px
   * phone puts the faces at a few dozen pixels, which cannot answer "is that
   * me" any better than the thumbnail did, so the viewer has to let people in
   * closer and pan around.
   */
  const [browseZoom, setBrowseZoom] = useState(false);
  const browseZoomRef = useRef<HTMLDivElement | null>(null);

  // Ask once on mount purely to know whether to offer it. The endpoint answers
  // enabled:false cheaply, so this costs nothing when browsing is off.
  useEffect(() => { if (browseSupported) loadBrowse(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // A new photo always opens fitted. Carrying a zoom across photos leaves
  // people looking at a corner of an image they have not seen whole.
  useEffect(() => { setBrowseZoom(false); }, [browseLightbox]);

  useEffect(() => {
    if (browseLightbox === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setBrowseLightbox(null);
      else if (e.key === 'ArrowLeft') void moveLightbox(-1);
      else if (e.key === 'ArrowRight') void moveLightbox(1);
    };
    window.addEventListener('keydown', onKey);
    // Without this the page behind scrolls under the photo on a phone, which
    // reads as the overlay itself being broken.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [browseLightbox, browsePhotos.length, browseCursor, browseLoading]); // eslint-disable-line react-hooks/exhaustive-deps
  const [pickedPossible, setPickedPossible] = useState<Set<string>>(new Set());
  const [selfieS3Key, setSelfieS3Key] = useState('');
  const [downloadToken, setDownloadToken] = useState<string | null>(initialDownloadToken || null);
  /** The emailed link's token was refused by the download endpoint. */
  const [linkRefused, setLinkRefused] = useState(false);
  const [issuingToken, setIssuingToken] = useState(false);
  const [freeEmail, setFreeEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  /** Shown while we are waiting out a busy server, so the spinner is not silent. */
  const [busyNotice, setBusyNotice] = useState<string | null>(null);

  // Pre-purchase verification state
  const [verifyEmail, setVerifyEmail] = useState(initialEmail || '');
  /**
   * Set when this address's pre-purchase covers the whole race: it is collected
   * on the race's photo page (which searches every album), not on this album's
   * link, whose downloads could never open it (lib/race-photos/redeem.ts).
   */
  const [eventLink, setEventLink] = useState<{ href: string; message: string } | null>(null);
  const [verifyCodeId, setVerifyCodeId] = useState<string | null>(null);
  const [verifyCodeInput, setVerifyCodeInput] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  // A pre-purchase the hub already found on this entry needs no email code.
  const [isPrePurchased, setIsPrePurchased] = useState(!!redeemPrePurchase);
  const [redeeming, setRedeeming] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState<string | null>(null);

  /**
   * What the athlete already owns through this finder: the token value they
   * arrived with and the photos it holds. Kept across "Try another selfie", so
   * a new search offers only what is new (owned-photos.ts) and there is a way
   * back to the photos they paid for.
   */
  const [owned, setOwned] = useState<{ token: string; ids: string[] } | null>(null);
  /** Said over the results when a search found photos they already own. */
  const [ownedNotice, setOwnedNotice] = useState<string | null>(null);

  // If arriving with a download token (from email link, or the hub), fetch
  // photo IDs. Also when the token CHANGES: the hub reloads after a purchase
  // and hands back every token the athlete holds for this album, so "your
  // photos" shows all of them, not only the purchase just made.
  const loadOwned = async (tokenValue: string): Promise<'ok' | 'refused' | 'failed'> => {
    try {
      const res = await fetch(`${base}/download?token=${tokenValue}`, { headers: photoRequestHeaders(false) });
      if (res.status === 403) return 'refused';
      if (!res.ok) return 'failed';
      const data = await res.json();
      const ids: string[] = (data.photos || []).map((p: { id: string }) => p.id);
      if (ids.length > 0) {
        setMatchedPhotoIds(ids);
        setOwned({ token: tokenValue, ids });
      }
      return 'ok';
    } catch (err) {
      console.error('Failed to load purchased photos:', err);
      return 'failed';
    }
  };

  useEffect(() => {
    if (!initialDownloadToken) return;
    setDownloadToken(initialDownloadToken);
    // Refused (expired, refunded, or not this link's): said by the grid
    // (DownloadLinkRefused), never left as "0 photos found".
    void loadOwned(initialDownloadToken).then((r) => { if (r === 'refused') setLinkRefused(true); });
  }, [base, initialDownloadToken]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * A token just issued (a purchase, a free download, a redemption). Beside
   * photos they already own, both are kept (download-tokens.ts), so the grid
   * shows everything they have rather than only what they just unlocked.
   */
  const adoptToken = (token: string) => {
    setOwnedNotice(null);
    if (!owned) {
      setDownloadToken(token);
      return;
    }
    const value = joinDownloadTokens([token, owned.token]) ?? token;
    setDownloadToken(value);
    void loadOwned(value);
  };

  /** Back to the photos they own, from a search or the camera. */
  const showOwnedPhotos = (notice: string | null = null) => {
    if (!owned) return;
    setDownloadToken(owned.token);
    setMatchedPhotoIds(owned.ids);
    setPreviews([]);
    setPossibleMatches([]);
    setPickedPossible(new Set());
    setLinkRefused(false);
    setError(null);
    setOwnedNotice(notice);
    setStep('results');
  };

  /**
   * Every photo the athlete has claimed as theirs: what the search matched
   * confidently, plus each "might also be you" they ticked. Browse picks are
   * already folded into matchedPhotoIds when they tap "add to my photos".
   *
   * Derived ONCE and used by the grid, the free download and the paid purchase
   * alike, because those three had drifted apart. The grid counted the ticked
   * maybes; both purchase calls sent the bare search results. So an athlete
   * whose face matched nothing confidently, ticked three maybes and typed their
   * email sent an EMPTY list, which the server rejects with "matched photos
   * required", and saw "Could not prepare your downloads" however many times
   * they tried. Anyone with at least one confident match simply lost their
   * ticked maybes silently, which is why it looked like it worked for most
   * people.
   */
  const claimed = useMemo(
    // The same merge browsing uses, because it is the same question: these
    // photos ADD to what the search found, they never replace it.
    () => mergePickedPhotos(matchedPhotoIds, previews, possibleMatches, pickedPossible),
    [matchedPhotoIds, previews, possibleMatches, pickedPossible],
  );
  const claimedPhotoIds = claimed.matchedPhotoIds;
  const claimedPreviews = claimed.previews;

  // Free albums issue a download token without payment; the server emails the
  // participant a copy of the download link. Resolves to null on success, or to
  // the reason it failed so the athlete is told something they can act on.
  const issueFreeDownloadToken = async (email: string): Promise<string | null> => {
    try {
      const res = await fetch(`${base}/purchase`, {
        method: 'POST',
        headers: photoRequestHeaders(),
        body: JSON.stringify({ selfieS3Key, matchedPhotoIds: claimedPhotoIds, email }),
      });
      if (!res.ok) {
        // Keep the server's reason, as the paid form already does. A rate
        // limit, an unpublished album and a genuine failure need different
        // things from the athlete, and one flat sentence told them nothing.
        const detail = await res.json().catch(() => null);
        return (
          detail?.message
          || (res.status === 429
            ? 'Too many requests just now. Please wait a moment and try again.'
            : 'Could not prepare your downloads. Please try again.')
        );
      }
      const data = await res.json();
      if (!data.downloadToken) return 'Could not prepare your downloads. Please try again.';
      adoptToken(data.downloadToken);
      onPhotosUnlocked?.();
      return null;
    } catch {
      return 'Could not reach us just now. Please check your connection and try again.';
    }
  };

  const handleFreeEmailSubmit = async () => {
    const email = freeEmail.trim();
    if (!email) return;
    if (claimedPhotoIds.length === 0) {
      setError('Pick at least one photo first.');
      return;
    }
    setIssuingToken(true);
    setError(null);
    const failure = await issueFreeDownloadToken(email);
    setIssuingToken(false);
    if (failure) {
      setError(failure);
    } else {
      setStep('results');
    }
  };

  const handleSelfieCapture = async (dataUrl: string) => {
    setStep('searching');
    setError(null);
    setBusyNotice(null);

    try {
      // Waits through a busy server rather than failing the athlete. A 503 here
      // means we could not look right now, not that their photos are missing,
      // and those must never look the same on screen.
      const res = await fetchThroughBusy(
        `${base}/selfie`,
        {
          method: 'POST',
          headers: photoRequestHeaders(),
          body: JSON.stringify({ selfieDataUrl: dataUrl, consent }),
        },
        {
          onWaiting: ({ waitMs }) =>
            setBusyNotice(`Lots of people are searching right now. Trying again in ${Math.round(waitMs / 1000)}s…`),
        },
      );
      setBusyNotice(null);

      if (!res.ok) {
        // Keep the server's reason: a rate limit, an unpublished album and a
        // genuine failure are very different messages to show a participant.
        const detail = await res.json().catch(() => null);
        // Closed between page load and this search: show the closed state.
        if (detail?.faceSearchClosed) {
          setFaceSearchClosed(true);
          setStep('selfie');
          return;
        }
        throw new Error(
          detail?.message
            || (res.status === 429
              ? 'Too many searches. Please wait a moment and try again.'
              : 'Could not search for your photos. Please try again.'),
        );
      }

      const data = await res.json();
      setSelfieS3Key(data.selfieS3Key);
      const foundIds: string[] = data.matchedPhotoIds || [];
      const foundPossible = ((data.possibleMatches || []) as { id: string; previewUrl: string | null; filename: string | null }[])
        .map((p) => ({ ...p, filename: p.filename || 'Race photo' }));

      // Someone who already owns photos here is offered only what is new, and
      // a search that finds nothing new takes them back to what they have
      // (owned-photos.ts). It used to sell them their own photos again.
      const split = owned
        ? splitSearchByOwned({ matchedPhotoIds: foundIds, possibleIds: foundPossible.map((p) => p.id), ownedIds: owned.ids })
        : null;
      if (split?.nothingNew) {
        showOwnedPhotos('Nothing new this time: every photo that search found is already yours.');
        return;
      }
      const matched = split ? split.newMatched : foundIds;
      const possible = split ? foundPossible.filter((p) => split.newPossible.includes(p.id)) : foundPossible;
      setPreviews(((data.previews || []) as { id: string; previewUrl: string | null; filename: string }[]).filter((p) => matched.includes(p.id)));
      setPossibleMatches(possible);
      setPickedPossible(new Set());
      setOwnedNotice(split ? alreadyOwnedNote(split.alreadyOwned) : null);

      // "No confident matches" is not the same as "nothing to show". Someone
      // whose face only matches weakly is precisely who the possible-match
      // panel exists for, and sending them to no_results hides it from the one
      // person who needs it.
      if (matched.length === 0 && possible.length === 0) {
        setStep('no_results');
      } else {
        setMatchedPhotoIds(matched);

        setStep('results');
        // A pre-purchase in the hub: nothing to decide when every match is a
        // confident one, so unlock straight away. With "might also be you"
        // photos the athlete ticks first and taps Get my photos.
        if (redeemPrePurchase && possible.length === 0 && matched.length > 0) {
          void runRedeem(matched, data.selfieS3Key);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Something went wrong');
      setStep('selfie');
    }
  };

  const runRedeem = async (ids: string[], selfieKey: string) => {
    if (!redeemPrePurchase) return;
    setRedeeming(true);
    setError(null);
    try {
      const token = await redeemPrePurchase(ids, selfieKey);
      adoptToken(token);
      onPhotosUnlocked?.();
    } catch (err: unknown) {
      setError((err instanceof Error && err.message) || 'Could not unlock your photos. Please try again.');
    } finally {
      setRedeeming(false);
    }
  };

  const handleRequestDownload = async () => {
    setError(null);
    // Already paid for: redeem what they have claimed, nothing to buy.
    if (redeemPrePurchase) {
      if (claimedPhotoIds.length === 0) { setError('Pick at least one photo first.'); return; }
      await runRedeem(claimedPhotoIds, selfieS3Key);
      return;
    }
    // Free albums collect an email so we can send a copy of the download link;
    // paid albums collect it in the payment form. Signed in, we have it.
    if (participantPrice === 0 && knownEmail) {
      if (claimedPhotoIds.length === 0) { setError('Pick at least one photo first.'); return; }
      setIssuingToken(true);
      const failure = await issueFreeDownloadToken(knownEmail);
      setIssuingToken(false);
      if (failure) setError(failure);
      return;
    }
    setStep(participantPrice === 0 ? 'free_email' : 'payment');
  };

  const handleRetakeSelfie = () => {
    setLinkRefused(false);
    setMatchedPhotoIds([]);
    setPreviews([]);
    setDownloadToken(null);
    setError(null);
    setOwnedNotice(null);
    setStep('selfie');
  };

  const handlePurchaseSuccess = (token: string) => {
    adoptToken(token);
    setStep('results');
    onPhotosUnlocked?.();
  };

  const handleVerifyEmail = async () => {
    if (!verifyEmail.trim()) return;
    setVerifyLoading(true);
    setError(null);

    try {
      const res = await fetch(`${base}/verify-email`, {
        method: 'POST',
        headers: photoRequestHeaders(),
        body: JSON.stringify({ email: verifyEmail.trim() }),
      });

      const data = await res.json();

      if (!data.prePurchased) {
        // Not a pre-purchaser - go to normal selfie flow
        setIsPrePurchased(false);
        setStep('selfie');
        return;
      }

      if (data.eventWide && data.eventLink) {
        setEventLink({ href: data.eventLink, message: data.message || 'Your photos are collected on the race photo page.' });
        return;
      }

      if (data.albumReady === false) {
        setError(data.message || 'Your photos are not ready yet.');
        return;
      }

      setIsPrePurchased(true);
      setVerifyCodeId(data.codeId);
      setMaskedEmail(data.maskedEmail);
      setStep('verify_code');
    } catch (err: any) {
      setError('Something went wrong. Please try again.');
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verifyCodeId || !verifyCodeInput.trim()) return;
    setVerifyLoading(true);
    setError(null);

    try {
      // Verify the code via the standard route
      const res = await fetch('/api/verification/verify-code', {
        method: 'POST',
        headers: photoRequestHeaders(),
        body: JSON.stringify({ codeId: verifyCodeId, code: verifyCodeInput.trim() }),
      });

      if (!res.ok) {
        const data = await res.json();
        setError(data.message || 'Invalid code. Please try again.');
        return;
      }

      // Code verified - now proceed to selfie capture
      // The selfie handler will use the redeem endpoint instead of purchase
      setStep('selfie');
    } catch (err: any) {
      setError('Something went wrong. Please try again.');
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleSelfieForRedemption = async (dataUrl: string) => {
    setStep('searching');
    setError(null);

    try {
      const res = await fetch(`${base}/selfie`, {
        method: 'POST',
        headers: photoRequestHeaders(),
        body: JSON.stringify({ selfieDataUrl: dataUrl, consent }),
      });

      if (!res.ok) {
        // Keep the server's reason: a rate limit, an unpublished album and a
        // genuine failure are very different messages to show a participant.
        const detail = await res.json().catch(() => null);
        // Closed between page load and this search: show the closed state.
        if (detail?.faceSearchClosed) {
          setFaceSearchClosed(true);
          setStep('selfie');
          return;
        }
        throw new Error(
          detail?.message
            || (res.status === 429
              ? 'Too many searches. Please wait a moment and try again.'
              : 'Could not search for your photos. Please try again.'),
        );
      }

      const data = await res.json();
      setSelfieS3Key(data.selfieS3Key);
      setPreviews(data.previews || []);
      setPossibleMatches(((data.possibleMatches || []) as { id: string; previewUrl: string | null; filename: string | null }[])
        .map((p) => ({ ...p, filename: p.filename || 'Race photo' })));
      setPickedPossible(new Set());

      // As above: weak-only matches still have something to offer.
      if (data.matchCount === 0 && (data.possibleMatches || []).length === 0) {
        setStep('no_results');
        return;
      }

      setMatchedPhotoIds(data.matchedPhotoIds);

      // Redeem the pre-purchase
      const redeemRes = await fetch(`${base}/redeem`, {
        method: 'POST',
        headers: photoRequestHeaders(),
        body: JSON.stringify({
          codeId: verifyCodeId,
          code: verifyCodeInput.trim(),
          matchedPhotoIds: data.matchedPhotoIds,
          selfieS3Key: data.selfieS3Key,
        }),
      });

      // A refusal is said, not swallowed: this went on to the results with no
      // token, which offered to sell the photos to someone who had paid.
      if (!redeemRes.ok) {
        const detail = await redeemRes.json().catch(() => null);
        if (detail?.code === 'EVENT_WIDE' && detail.eventLink) {
          setEventLink({ href: detail.eventLink, message: detail.message });
          setStep('verify_email');
          return;
        }
        throw new Error(detail?.message || 'We could not unlock your pre-purchased photos. Please try again.');
      }
      const redeemData = await redeemRes.json();
      adoptToken(redeemData.downloadToken);
      onPhotosUnlocked?.();

      setStep('results');
    } catch (err: any) {
      setError(err.message || 'Something went wrong');
      setStep('selfie');
    }
  };

  const priceLabel = participantPrice === 0
    ? 'Free download'
    : `${formatPrice(participantPrice, participantPriceCurrency)} per person`;

  // Left-aligned in both: a centred column on the public page, the hub's
  // hairline layout on the ticket.
  const align = 'left' as const;

  return (
    <div>
      {hub ? (
        // The ticket already names the race, so no banner: the price, once,
        // and only while there is still something to pay for.
        !downloadToken && !isPrePurchased && (
          <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--fvd-ink-muted)' }}>
            {participantPrice === 0
              ? 'Free to download.'
              : `${formatPrice(participantPrice, participantPriceCurrency)} for all of your photos, in full resolution.`}
          </p>
        )
      ) : (
        <PhotoEventHero
          eventName={eventName}
          eyebrow="Race photos"
          subtitle={faceSearchClosed ? 'Browse the photos and pick yours.' : 'Take a selfie and find your photos in seconds.'}
          // The price only while there is still something to pay for.
          badge={downloadToken || isPrePurchased ? undefined : priceLabel}
          coverImageUrl={coverImageUrl}
          mobileCoverImageUrl={mobileCoverImageUrl}
          bannerPosition={bannerPosition}
          primary={themeColors.primary}
          secondary={themeColors.secondary}
        />
      )}

      {/* Content */}
      <div style={hub ? { padding: '14px 2px 8px' } : PAGE_COLUMN}>
        {/* A search away from the photos they already own: the way back. */}
        {owned && !downloadToken && (step === 'selfie' || step === 'results' || step === 'no_results') && (
          <BackToPhotos variant={variant} label="Back to your photos" onClick={() => showOwnedPhotos()} />
        )}

        {/* Step: Verify Email (pre-purchase redemption) */}
        {step === 'verify_email' && (
          <div style={{ textAlign: align }}>
            <StepHeading variant={variant}>{eventLink ? 'Your photos are on the race page' : 'Verify your email'}</StepHeading>
            <StepText variant={variant}>
              {eventLink ? eventLink.message : 'Enter the email you used when registering to access your pre-purchased photos.'}
            </StepText>
            {error && <StepError variant={variant}>{error}</StepError>}
            {eventLink ? (
              <a
                href={eventLink.href}
                className="fvd-hoverable-solid"
                style={{ ...stepPrimaryStyle(variant, { isMobile, themeColors }), boxSizing: 'border-box' }}
              >
                Collect my photos
              </a>
            ) : (
            <form onSubmit={(e) => { e.preventDefault(); handleVerifyEmail(); }} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <input
                type="email"
                value={verifyEmail}
                onChange={(e) => setVerifyEmail(e.target.value)}
                placeholder="your@email.com"
                aria-label="Email"
                required
                style={stepInputStyle(variant, isMobile)}
              />
              <EmailTypoHint email={verifyEmail} onAccept={setVerifyEmail} accentColor="var(--fvd-teal-text)" />
              <button
                type="submit"
                disabled={verifyLoading || !verifyEmail.trim()}
                className="fvd-hoverable-solid"
                style={stepPrimaryStyle(variant, { isMobile, disabled: verifyLoading || !verifyEmail.trim(), themeColors })}
              >
                {verifyLoading ? 'Checking...' : 'Continue'}
              </button>
            </form>
            )}
            <button
              type="button"
              onClick={() => { setIsPrePurchased(false); setEventLink(null); setStep('selfie'); }}
              style={stepLinkStyle(variant)}
            >
              I haven&apos;t pre-purchased, find and buy photos instead
            </button>
          </div>
        )}

        {/* Step: Verify Code */}
        {step === 'verify_code' && (
          <div style={{ textAlign: align }}>
            <StepHeading variant={variant}>Enter verification code</StepHeading>
            <StepText variant={variant}>
              We sent a 6-digit code to {maskedEmail || 'your email'}
            </StepText>
            {error && <StepError variant={variant}>{error}</StepError>}
            <form onSubmit={(e) => { e.preventDefault(); handleVerifyCode(); }} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <HubCodeInput
                value={verifyCodeInput}
                onChange={setVerifyCodeInput}
                busy={verifyLoading}
                ariaLabel="Verification code"
                autoFocus
              />
              <button
                type="submit"
                disabled={verifyLoading || verifyCodeInput.length !== 6}
                className="fvd-hoverable-solid"
                style={stepPrimaryStyle(variant, { isMobile, disabled: verifyLoading || verifyCodeInput.length !== 6, themeColors })}
              >
                {verifyLoading ? 'Verifying...' : 'Verify and find my photos'}
              </button>
            </form>
            <button
              type="button"
              onClick={() => { setStep('verify_email'); setVerifyCodeInput(''); setError(null); }}
              style={stepLinkStyle(variant)}
            >
              Use a different email
            </button>
          </div>
        )}

        {/* Step: Selfie, or what replaces it once face search has closed */}
        {step === 'selfie' && faceSearchClosed && (
          <div style={{ textAlign: align }}>
            <StepHeading variant={variant}>Face search has closed</StepHeading>
            <StepText variant={variant}>
              Face data for this event has been deleted, as our privacy notice promises.
            </StepText>
          </div>
        )}
        {step === 'selfie' && !faceSearchClosed && (
          <div style={{ textAlign: align }}>
            {consent && (
              <>
                <StepHeading variant={variant}>Take a selfie</StepHeading>
                <StepText variant={variant}>
                  We match your face with the event photos.
                </StepText>
              </>
            )}
            {error && <StepError variant={variant}>{error}</StepError>}
            {!consent ? (
              // Consent is taken, and recorded, before the camera opens.
              <PhotoConsentGate themeColors={themeColors} onConsent={setConsent} variant={variant} />
            ) : (
              <SelfieCapture
                variant={variant}
                onCapture={isPrePurchased && !redeemPrePurchase ? handleSelfieForRedemption : handleSelfieCapture}
              />
            )}
            {!isPrePurchased && participantPrice > 0 && supportsEmailLookup && (
              <button
                type="button"
                onClick={() => { setStep('verify_email'); }}
                style={stepLinkStyle(variant)}
              >
                I pre-purchased my photos
              </button>
            )}
          </div>
        )}

        {/* Step: Searching */}
        {step === 'searching' && (
          <div role="status" style={{ padding: '18px 0' }}>
            <StepSpinner variant={variant} />
            <StepHeading variant={variant}>Searching for your photos</StepHeading>
            <StepText variant={variant} style={{ margin: 0 }}>
              {busyNotice || 'This usually takes a few seconds.'}
            </StepText>
          </div>
        )}

        {/* Step: No Results */}
        {step === 'no_results' && (
          <div style={{ textAlign: align, padding: '6px 0 0' }}>
            <StepHeading variant={variant}>No photos found</StepHeading>
            <StepText variant={variant}>
              We couldn&apos;t find any photos matching your face. Try again with better lighting.
            </StepText>
            <button
              type="button"
              onClick={() => setStep('selfie')}
              className="fvd-hoverable-solid"
              style={stepPrimaryStyle(variant, { isMobile, themeColors, fullWidth: false })}
            >
              Try again
            </button>
          </div>
        )}

        {/* Free downloads, a redemption or a purchase that could not finish,
            said where the athlete is looking: over their photos. */}
        {step === 'results' && error && <StepError variant={variant}>{error}</StepError>}
        {step === 'results' && ownedNotice && <StepText variant={variant}>{ownedNotice}</StepText>}
        {/* Step: Results */}
        {step === 'results' && (
          <PhotoResultsGrid
            albumId={albumId}
            apiBase={base}
            matchedPhotoIds={claimedPhotoIds}
            previews={claimedPreviews}
            possibleMatches={possibleMatches}
            pickedPossible={pickedPossible}
            onTogglePossible={(id: string) => setPickedPossible((prev) => {
              const next = new Set(prev);
              if (next.has(id)) next.delete(id); else next.add(id);
              return next;
            })}
            downloadToken={downloadToken}
            participantPrice={participantPrice}
            participantPriceCurrency={participantPriceCurrency}
            onRequestDownload={handleRequestDownload}
            onRetakeSelfie={handleRetakeSelfie}
            themeColors={themeColors}
            variant={variant}
            prePaid={!!redeemPrePurchase}
            unlocking={issuingToken || redeeming}
            linkRefused={linkRefused && downloadToken === initialDownloadToken}
          />
        )}

        {/* Browsing belongs here too, not only at the dead end.
            "I found two photos but I know there were more of me" is at least as
            common as finding nothing: a group shot the cap never indexed, or a
            genuine match the threshold had to reject. Offering it only after a
            total miss serves the rarer case and leaves the commoner one stuck. */}
        {(step === 'results' || step === 'no_results' || (step === 'selfie' && faceSearchClosed)) && browseEnabled && (
          // With face search closed, browsing is the page's only action: it
          // follows the message directly, as the primary button, with no
          // divider setting it apart from what it answers.
          <div style={step === 'selfie' ? { marginTop: 4 } : {
            marginTop: step === 'results' ? 22 : 8,
            paddingTop: 16,
            borderTop: '1px solid var(--fvd-hairline-light)',
          }}>
            {!browseOpen ? (
              <div>
                {step !== 'selfie' && (
                  <p style={{ fontSize: 13, color: 'var(--fvd-ink-secondary)', lineHeight: 1.55, margin: '0 0 10px' }}>
                    {step === 'no_results'
                      ? 'Sure you were photographed? Some photos have no clear face for us to match.'
                      : 'Think we missed some? Not every photo has a face we can match.'}
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => setBrowseOpen(true)}
                  className={step === 'selfie' ? 'fvd-hoverable-solid' : 'fvd-hoverable'}
                  style={hubButtonStyle(step === 'selfie' ? 'primary' : 'secondary', isMobile, { borderRadius: 'var(--fv-radius-button, 10px)' })}
                >
                  Browse all photos
                </button>
              </div>
            ) : (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                  <StepHeading variant={variant} style={{ margin: 0 }}>All photos</StepHeading>
                  <button
                    type="button"
                    onClick={() => { setBrowseOpen(false); setPickedBrowse(new Set()); }}
                    style={stepLinkStyle(variant, { marginTop: 0 })}
                  >
                    Close
                  </button>
                </div>
                <StepText variant={variant} style={{ margin: '0 0 14px' }}>
                  {isMobile ? 'Tap a photo to see it bigger, then add it to yours.' : 'Click a photo to see it bigger, then add it to yours.'}
                </StepText>

                <div style={{
                  display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(112px, 1fr))', gap: 8,
                  // Clear of the fixed action bar, so the last row is reachable.
                  paddingBottom: pickedBrowse.size > 0 ? 84 : 0,
                }}>
                  {browsePhotos.map((photo, i) => {
                    const picked = pickedBrowse.has(photo.id);
                    const already = matchedPhotoIds.includes(photo.id);
                    return (
                      <div key={photo.id} style={{ position: 'relative', aspectRatio: '4/3' }}>
                        <button
                          type="button"
                          // Opens the lightbox rather than selecting. Deciding
                          // from a 120px tile is the thing this panel exists to
                          // avoid, so tapping the photo means "let me look",
                          // and the tick is the separate "that's me".
                          onClick={() => setBrowseLightbox(i)}
                          aria-label={`View ${photo.filename} larger`}
                          style={{
                            width: '100%', height: '100%', padding: 0, cursor: 'pointer', lineHeight: 0,
                            borderRadius: 'var(--fv-radius-tile, 8px)', overflow: 'hidden', display: 'block',
                            background: 'var(--fvd-neutral-chip)', border: 'none',
                            boxShadow: picked ? '0 0 0 2px var(--fvd-teal)' : 'inset 0 0 0 1px var(--fvd-hairline)',
                            opacity: already ? 0.5 : 1,
                          }}
                        >
                          {photo.previewUrl && (
                            <img src={photo.previewUrl} alt={photo.filename} loading="lazy"
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          )}
                        </button>
                        {already ? (
                          <span
                            role="img"
                            aria-label="Already in your photos"
                            title="Already in your photos"
                            style={{ position: 'absolute', top: 6, left: 6, display: 'flex' }}
                          >
                            <TileTick on><Check size={14} strokeWidth={2.5} /></TileTick>
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => toggleBrowsePick(photo.id)}
                            aria-pressed={picked}
                            aria-label={picked ? `Remove ${photo.filename}` : `Add ${photo.filename}`}
                            style={{
                              position: 'absolute', top: 0, left: 0, width: 40, height: 40,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                            }}
                          >
                            <TileTick on={picked}><Check size={14} strokeWidth={2.5} /></TileTick>
                          </button>
                        )}
                      </div>
                    );
                  })}
                  {/* The next page arriving, shaped like the tiles it becomes. */}
                  {browseLoading && Array.from({ length: browsePhotos.length ? 6 : 9 }).map((_, i) => (
                    <div key={`bone-${i}`} aria-hidden="true" style={{ aspectRatio: '4/3' }}>
                      <HubSkeleton height={0} radius={8} style={{ height: '100%', borderRadius: 'var(--fv-radius-tile, 8px)' }} />
                    </div>
                  ))}
                </div>

                {browseCursor && !browseLoading && (
                  <div style={{ marginTop: 14 }}>
                    <button
                      type="button"
                      onClick={() => loadBrowse(browseCursor)}
                      className="fvd-hoverable"
                      style={hubButtonStyle('secondary', isMobile, { borderRadius: 'var(--fv-radius-button, 10px)' })}
                    >
                      Load more
                    </button>
                  </div>
                )}

                {/* Fixed to the viewport, not sticky at the end of the grid.
                    Sticky put the bar wherever the scroll happened to be, so it
                    sat among the thumbnails and read as another tile. The one
                    control that finishes the job cannot be something you have to
                    find. */}
                {pickedBrowse.size > 0 && (
                  <div style={{
                    position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 50,
                    // Its own ground, so photos never show through the one
                    // element that has to stay readable: the canvas the flow
                    // sits on (the organiser's page colour on a public page).
                    background: 'var(--fvd-canvas)',
                    borderTop: '1px solid var(--fvd-hairline)',
                    padding: '10px 16px calc(env(safe-area-inset-bottom) + 10px)',
                  }}>
                    <button
                      type="button"
                      onClick={() => {
                        // Merge, never replace: this panel adds to what the
                        // search already found.
                        const merged = mergePickedPhotos(matchedPhotoIds, previews, browsePhotos, pickedBrowse);
                        setMatchedPhotoIds(merged.matchedPhotoIds);
                        setPreviews(merged.previews);
                        setPickedBrowse(new Set());
                        setBrowseOpen(false);
                        setStep('results');
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className="fvd-hoverable-solid"
                      style={hubButtonStyle('primary', isMobile, { display: 'flex', width: '100%', maxWidth: 460, margin: '0 auto', borderRadius: 'var(--fv-radius-button, 10px)' })}
                    >
                      Add {pickedBrowse.size} photo{pickedBrowse.size === 1 ? '' : 's'} to my photos
                    </button>
                  </div>
                )}

                {/* Full-size viewer. A 120px tile cannot answer the only
                    question browsing asks, so looking properly and choosing
                    have to happen in the same place: the tick lives in here,
                    not back in the grid. */}
                {browseLightbox !== null && browsePhotos[browseLightbox] && (() => {
                  const photo = browsePhotos[browseLightbox];
                  const picked = pickedBrowse.has(photo.id);
                  const already = matchedPhotoIds.includes(photo.id);
                  const hasNext = browseLightbox < browsePhotos.length - 1 || !!browseCursor;
                  return (
                    <div
                      role="dialog"
                      aria-modal="true"
                      aria-label="Photo viewer"
                      onClick={() => setBrowseLightbox(null)}
                      onTouchStart={(e) => { browseTouchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; }}
                      onTouchEnd={(e) => {
                        const start = browseTouchRef.current;
                        browseTouchRef.current = null;
                        if (!start) return;
                        const dx = e.changedTouches[0].clientX - start.x;
                        const dy = e.changedTouches[0].clientY - start.y;
                        // Horizontal only, and clearly horizontal, so a scroll
                        // attempt does not skip the photo being looked at.
                        // While zoomed a drag is a pan, not a page turn.
                        if (browseZoom) return;
                        if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) void moveLightbox(dx < 0 ? 1 : -1);
                      }}
                      style={{
                        // Opaque, not a scrim: at 6% pass-through the grid of
                        // thumbnails behind still reads clearly and competes with the
                        // photo being judged, which is the one thing this view is for.
                        position: 'fixed', inset: 0, zIndex: 60, ...viewerSurfaceStyle(),
                        display: 'flex', flexDirection: 'column', fontFamily: 'inherit',
                        paddingBottom: 'env(safe-area-inset-bottom)', paddingTop: 'env(safe-area-inset-top)',
                      }}
                    >
                      <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '8px 12px', flexShrink: 0,
                      }}>
                        <span style={{ fontSize: 13, color: 'var(--pv-ink-soft, rgba(255,255,255,0.72))', fontFamily: 'var(--fvd-font-mono)', fontVariantNumeric: 'tabular-nums' }}>
                          {browseLightbox + 1} of {browsePhotos.length}{browseCursor ? '+' : ''}
                        </span>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setBrowseLightbox(null); }}
                          aria-label="Close viewer"
                          style={viewerControlStyle()}
                        >
                          <X size={20} />
                        </button>
                      </div>

                      <div
                        ref={browseZoomRef}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          flex: 1, minHeight: 0, padding: '0 8px',
                          display: browseZoom ? 'block' : 'flex',
                          alignItems: 'center', justifyContent: 'center',
                          overflow: browseZoom ? 'auto' : 'hidden',
                          WebkitOverflowScrolling: 'touch',
                        }}
                      >
                        <img
                          src={photo.fullPreviewUrl || photo.previewUrl || ''}
                          alt={photo.filename}
                          onClick={() => {
                            const next = !browseZoom;
                            setBrowseZoom(next);
                            // Land in the middle of the photo rather than its
                            // top-left corner, which is rarely where anyone is.
                            if (next) requestAnimationFrame(() => {
                              const box = browseZoomRef.current;
                              if (!box) return;
                              box.scrollLeft = (box.scrollWidth - box.clientWidth) / 2;
                              box.scrollTop = (box.scrollHeight - box.clientHeight) / 2;
                            });
                          }}
                          style={browseZoom
                            ? { width: '260%', maxWidth: 'none', height: 'auto', display: 'block', cursor: 'zoom-out' }
                            : { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 'var(--fv-radius-tile, 6px)', cursor: 'zoom-in' }}
                        />
                      </div>

                      <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                          gap: 10, padding: 12,
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => void moveLightbox(-1)}
                          disabled={browseLightbox === 0}
                          aria-label="Previous photo"
                          style={{ ...viewerControlStyle(48), opacity: browseLightbox === 0 ? 0.35 : 1, cursor: browseLightbox === 0 ? 'default' : 'pointer' }}
                        >
                          <ChevronLeft size={20} />
                        </button>

                        {already ? (
                          <span style={{ ...viewerActionStyle(false), cursor: 'default', border: 'none', color: 'var(--pv-ink-soft, rgba(255,255,255,0.75))' }}>
                            Already in your photos
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => toggleBrowsePick(photo.id)}
                            aria-pressed={picked}
                            style={viewerActionStyle(picked)}
                          >
                            {picked && <Check size={16} strokeWidth={2.5} />}
                            {picked ? 'Added to my photos' : 'This is me'}
                          </button>
                        )}

                        <button
                          type="button"
                          onClick={() => void moveLightbox(1)}
                          disabled={!hasNext || browseLoading}
                          aria-label="Next photo"
                          style={{ ...viewerControlStyle(48), opacity: !hasNext || browseLoading ? 0.35 : 1, cursor: !hasNext || browseLoading ? 'default' : 'pointer' }}
                        >
                          <ChevronRight size={20} />
                        </button>
                      </div>

                      <p style={{
                        flexShrink: 0, textAlign: 'center', margin: '0 0 10px',
                        fontSize: 12, color: 'var(--pv-ink-soft, rgba(255,255,255,0.6))', padding: '0 16px',
                      }}>
                        {browseZoom
                          ? `${isMobile ? 'Tap' : 'Click'} the photo to zoom back out.`
                          : pickedBrowse.size > 0
                            ? `${pickedBrowse.size} selected. Close to add them to your photos.`
                            : `${isMobile ? 'Tap' : 'Click'} the photo to zoom in. The watermark comes off the ones you download.`}
                      </p>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {/* Step: Free album email capture */}
        {step === 'free_email' && (
          <div style={{ textAlign: align }}>
            <BackToPhotos variant={variant} onClick={() => { setError(null); setStep('results'); }} />
            <StepHeading variant={variant}>Where should we send your photos?</StepHeading>
            <StepText variant={variant}>
              Your downloads unlock straight away and we&apos;ll email you a copy of the link so you can come back any time.
            </StepText>
            {error && <StepError variant={variant}>{error}</StepError>}
            <form onSubmit={(e) => { e.preventDefault(); handleFreeEmailSubmit(); }} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <input
                type="email"
                value={freeEmail}
                onChange={(e) => setFreeEmail(e.target.value)}
                placeholder="your@email.com"
                aria-label="Email"
                required
                style={stepInputStyle(variant, isMobile)}
              />
              <EmailTypoHint email={freeEmail} onAccept={setFreeEmail} accentColor="var(--fvd-teal-text)" />
              <button
                type="submit"
                disabled={issuingToken || !freeEmail.trim()}
                className="fvd-hoverable-solid"
                style={stepPrimaryStyle(variant, { isMobile, disabled: issuingToken || !freeEmail.trim(), themeColors })}
              >
                {issuingToken ? 'Preparing your photos...' : 'Get my photos'}
              </button>
            </form>
          </div>
        )}

        {/* Step: Payment (inline) */}
        {step === 'payment' && (
          <div>
            <BackToPhotos variant={variant} onClick={() => setStep('results')} />
            <PhotoPurchaseForm
              albumId={albumId}
              apiBase={base}
              matchedPhotoIds={claimedPhotoIds}
              selfieS3Key={selfieS3Key}
              participantPrice={participantPrice}
              currency={participantPriceCurrency}
              themeColors={themeColors}
              onSuccess={handlePurchaseSuccess}
              variant={variant}
              knownEmail={knownEmail}
            />
          </div>
        )}
      </div>

      <style jsx global>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Transparency for everyone in the photos, not only the person
          searching: every face in every uploaded photo is indexed, so people in
          the background have biometric data processed too. */}
      <FacialRecognitionNotice
        removalHref={removalHref || `/photos/${albumToken}/remove`}
        selfRedactionEnabled={selfRedactionEnabled}
        variant={variant}
      />
    </div>
  );
}

function BackToPhotos({ onClick, label = 'Back to photos' }: { variant?: PhotoVariant; onClick: () => void; label?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 44, padding: 0, marginBottom: 6,
        background: 'none', border: 'none', fontFamily: 'inherit', cursor: 'pointer',
        color: 'var(--fvd-ink-secondary)', fontSize: 12.5, fontWeight: 500,
      }}
    >
      <ArrowLeft size={14} /> {label}
    </button>
  );
}
